/* Shared, section-scoped hero player. No negative playbackRate (unsupported on iOS). */
(() => {
  if (window.ALStoryScrollPlayer) { window.ALStoryScrollPlayer.scan(); return; }
  const instances = new Map();
  const clamp = n => Math.max(0, Math.min(1, n));
  const ease = (p, kind) => kind === 'cinematic' ? (1 - Math.cos(Math.PI * p)) / 2 : kind === 'soft' ? p * p * (3 - 2 * p) : p;

  function mount(root) {
    if (instances.has(root)) return;
    const controller = new AbortController();
    const on = (el, event, fn, options = {}) => el.addEventListener(event, fn, { ...options, signal: controller.signal });
    const panel = root.querySelector('[data-al-scroll]');
    const stage = root.querySelector('[data-al-stage]');
    const videos = [...root.querySelectorAll('[data-al-video]:not([data-al-echo])')];
    const echo = root.querySelector('[data-al-echo]');
    const desktop = matchMedia('(min-width: 990px)');
    const reduced = matchMedia('(prefers-reduced-motion: reduce)');
    const background = root.dataset.playbackMode === 'background';
    const boomerang = background && root.dataset.loopStyle !== 'forward';
    const speed = Number(root.dataset.loopSpeed) || 1;
    const exitStart = clamp((root.dataset.fadeDistance === undefined || root.dataset.fadeDistance === '' ? 35 : Number(root.dataset.fadeDistance)) / 100);
    const numberSetting = (name, fallback) => panel.dataset[name] === undefined || panel.dataset[name] === '' ? fallback : Number(panel.dataset[name]);
    const entryStart = clamp(numberSetting('entryStart', 0) / 100);
    const entryEnd = Math.max(entryStart, clamp(numberSetting('entryEnd', 100) / 100));
    const exitEnd = Math.min(exitStart, clamp(numberSetting('exitEnd', 0) / 100));
    const ramp = (value, start, end) => end === start ? (value >= end ? 1 : 0) : clamp((value - start) / (end - start));
    let visible = false, duration = 0, target = 0, phase = 0, raf = 0, last = 0, disposed = false;
    const primed = new WeakSet(), priming = new WeakSet();
    const start = () => { if (!raf && !disposed) raf = requestAnimationFrame(tick); };
    const seek = (video, time) => {
      if (!primed.has(video) || video.readyState < 2 || video.seeking || !Number.isFinite(video.duration)) return;
      const bounded = Math.max(0, Math.min(time, video.duration - .04));
      if (Math.abs(video.currentTime - bounded) > .018) video.currentTime = bounded;
    };
    function finishPrime(video) {
      if (disposed) { video.pause(); return; }
      video.pause(); priming.delete(video); primed.add(video); seek(video, target); start();
    }
    function prime(video) {
      if (disposed || primed.has(video) || priming.has(video) || reduced.matches) return;
      priming.add(video);
      video.muted = video.defaultMuted = true; video.playsInline = true;
      const attempt = video.play();
      if (!attempt) { finishPrime(video); return; }
      attempt.then(() => {
        if (disposed || reduced.matches) { video.pause(); priming.delete(video); return; }
        if (typeof video.requestVideoFrameCallback === 'function') {
          // Wait for an actual decoded frame before pausing on iPhone's first visit.
          let finished = false;
          const finish = () => { if (!finished) { finished = true; finishPrime(video); } };
          video.requestVideoFrameCallback(finish);
          setTimeout(finish, 250);
        } else finishPrime(video);
      }).catch(() => { priming.delete(video); });
    }
    function playForward(video) {
      if (!video.paused || priming.has(video)) return;
      priming.add(video); video.loop = true; video.muted = video.defaultMuted = true; video.playbackRate = speed;
      const attempt = video.play();
      if (attempt) attempt.then(() => { priming.delete(video); if (disposed || !visible || reduced.matches) video.pause(); }).catch(() => priming.delete(video));
      else priming.delete(video);
    }
    function prepare(video) {
      const ready = () => {
        if (Number.isFinite(video.duration)) duration = video.duration;
        update();
        if (!background || boomerang) prime(video);
      };
      on(video, 'loadedmetadata', ready);
      on(video, 'loadeddata', ready);
      on(video, 'seeked', () => { if (visible) start(); });
      if (video.readyState >= 1) ready();
    }
    function syncEcho() {
      if (!echo) return;
      if (desktop.matches && !videos.includes(echo)) {
        echo.src = videos[0].currentSrc || echo.dataset.alSrc; echo.preload = 'auto'; videos.push(echo); prepare(echo); echo.load();
      } else if (!desktop.matches && videos.includes(echo)) {
        echo.pause(); videos.splice(videos.indexOf(echo), 1);
      }
    }
    function tick(now) {
      raf = 0;
      if (!visible || document.hidden || disposed || reduced.matches) { last = 0; return; }
      const dt = last ? Math.min((now - last) / 1000, .05) : 0;
      last = now;
      if (boomerang && duration && primed.has(videos[0])) {
        // Wall-clock loop; each video allows only one pending seek to avoid decoder overload.
        phase = (phase + dt * speed / Math.max(.1, duration - .04)) % 2;
        const progress = phase <= 1 ? phase : 2 - phase;
        target = ease(progress, root.dataset.loopEasing) * Math.max(0, duration - .04);
      }
      if (!background || boomerang) videos.forEach(v => seek(v, target));
      if (boomerang && videos.length) start();
      else last = 0;
    }
    function update() {
      const rect = root.getBoundingClientRect();
      const viewport = innerHeight;
      visible = rect.top < viewport && rect.bottom > 0 && !document.hidden;
      const remaining = rect.bottom / Math.max(1, viewport);
      const exit = exitStart === exitEnd ? (remaining > exitEnd ? 1 : 0) : ramp(remaining, exitEnd, exitStart);
      // Fade the entire composition only at the physical exit, not before sticky release.
      panel.style.opacity = String(ease(exit, panel.dataset.fadeOutEasing));
      const travel = Math.max(1, root.offsetHeight - panel.offsetHeight);
      const progress = clamp(-rect.top / travel);
      // Same viewport-relative entrance in every playback mode, before sticky scrolling begins.
      // 0 = section top meets screen bottom; 1 = section top meets screen top.
      const entry = ramp((viewport - rect.top) / Math.max(1, viewport), entryStart, entryEnd);
      stage.style.opacity = String(reduced.matches ? 1 : ease(entry, panel.dataset.fadeInEasing));
      if (!background) target = progress * Math.max(0, duration - .04);
      stage.style.transform = reduced.matches || background ? 'none' : `scale(${1.02 + progress * .035})`;
      if (!visible || reduced.matches) {
        videos.forEach(v => v.pause()); cancelAnimationFrame(raf); raf = 0; last = 0;
      } else if (background && !boomerang) videos.forEach(playForward);
      else { videos.forEach(prime); start(); }
    }
    function gesture() { if (visible) { videos.forEach(background && !boomerang ? playForward : prime); start(); } }
    instances.set(root, () => { disposed = true; controller.abort(); cancelAnimationFrame(raf); videos.forEach(v => v.pause()); instances.delete(root); });
    videos.forEach(prepare); syncEcho();
    on(desktop, 'change', () => { syncEcho(); update(); });
    on(reduced, 'change', update);
    on(window, 'scroll', update, { passive: true });
    on(window, 'resize', update, { passive: true });
    on(document, 'visibilitychange', update);
    on(document, 'touchstart', gesture, { passive: true });
    on(document, 'pointerdown', gesture, { passive: true });
    update();
  }
  function scan() {
    instances.forEach((dispose, root) => { if (!root.isConnected) dispose(); });
    document.querySelectorAll('[data-al-scroll-player]').forEach(mount);
  }
  window.ALStoryScrollPlayer = { scan };
  document.addEventListener('shopify:section:load', scan);
  document.addEventListener('shopify:section:unload', event => instances.forEach((dispose, root) => { if (event.target.contains(root)) dispose(); }));
  scan();
})();
